/**
 * Cold-start-safe handoff for native OAuth account linking (Android especially).
 *
 * The native link flow opens the provider consent in the SYSTEM browser. While the user consents,
 * Android routinely kills the backgrounded WebView app under memory pressure; the returning
 * `app.lapo.client://…` deep link then COLD-STARTS a fresh app process. In that new process the
 * in-memory `runLink` promise, its transient `onOpenUrl` listener, and the OAuth `state` are all gone
 * (they lived only in the dead JS closure), so the `?code=` is dropped and the account never links —
 * even though the app visibly "came back". This is exactly why the app's sign-in flow avoids deep
 * links entirely (see {@link file://./mobileSignIn.ts}).
 *
 * The durable fix: before opening the browser we persist the pending link here (localStorage survives
 * process death), and on the next app startup we read the launch deep link via the deep-link plugin's
 * `getCurrent()` and finish the exchange against the same `/oauth/callback` redirect — no server or
 * Google-console changes. The warm-resume path (app survived → live `onOpenUrl` fires) still works and
 * clears the record on success; this only rescues the cold-start case.
 */
import { parseCallbackParams, type CallbackMessage } from './oauthRedirect.js';

export type PendingLinkProvider = 'google' | 'todoist';

export interface PendingLink {
  provider: PendingLinkProvider;
  /** The OAuth `state` from beginLink — correlates the returning deep link to this request. */
  state: string;
  /** Epoch ms; used to drop stale records (an auth code is short-lived). */
  savedAt: number;
}

const KEY = 'lapo.pendingLink';
/** OAuth codes expire fast; don't resume a record older than this (stale tab, abandoned flow). */
const TTL_MS = 15 * 60 * 1000;

/** The completion endpoints a resume needs — the subset of ApiClient used to finish each provider. */
export interface LinkCompletionApi {
  completeLink(state: string, code: string): Promise<unknown>;
  completeTodoistLink(state: string, code: string): Promise<unknown>;
}

export function savePendingLink(rec: { provider: PendingLinkProvider; state: string }): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...rec, savedAt: Date.now() } satisfies PendingLink));
  } catch {
    /* storage unavailable → the warm (live-promise) path still completes the link */
  }
}

export function loadPendingLink(): PendingLink | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as PendingLink;
    if (!rec || typeof rec.state !== 'string' || typeof rec.provider !== 'string') return null;
    if (Date.now() - (rec.savedAt ?? 0) > TTL_MS) {
      clearPendingLink();
      return null;
    }
    return rec;
  } catch {
    return null;
  }
}

export function clearPendingLink(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Default source of the launch deep-link URL(s): the plugin's cold-start API. Injectable for tests. */
async function readLaunchUrls(): Promise<string[] | null> {
  const { getCurrent } = await import('@tauri-apps/plugin-deep-link');
  return getCurrent();
}

export interface ResumeResult {
  provider: PendingLinkProvider;
}

/**
 * On startup (native shell, signed in), finish a link that was interrupted by a cold start: if the app
 * was launched by our OAuth deep link AND a matching pending record exists, exchange the code. Returns
 * the completed provider, or null when there's nothing to resume. Throws only on an actual completion
 * failure (so the caller can surface it). Safe to call on every boot — a no-op without a pending link.
 */
export async function resumePendingLink(
  api: LinkCompletionApi,
  getUrls: () => Promise<string[] | null> = readLaunchUrls,
): Promise<ResumeResult | null> {
  const pending = loadPendingLink();
  if (!pending) return null;

  const urls = await getUrls().catch(() => null);
  if (!urls || urls.length === 0) return null;

  let cb: CallbackMessage | undefined;
  for (const raw of urls) {
    let search = '';
    try {
      search = new URL(raw).search;
    } catch {
      continue; // not a URL we can parse
    }
    const msg = parseCallbackParams(search);
    if (msg.state === pending.state) {
      cb = msg;
      break;
    }
  }
  // Launch URL isn't our pending callback (normal icon launch, or a different flow): leave the record
  // for a later boot rather than discarding a still-valid pending link.
  if (!cb) return null;

  // It IS our callback — consume the record now so a failed retry can't loop.
  clearPendingLink();
  if (cb.error) throw new Error(`OAuth failed: ${cb.error}`);
  if (!cb.code) throw new Error('OAuth callback missing code');

  switch (pending.provider) {
    case 'google':
      await api.completeLink(pending.state, cb.code);
      break;
    case 'todoist':
      await api.completeTodoistLink(pending.state, cb.code);
      break;
  }
  return { provider: pending.provider };
}
